import { normalizeText } from "../../../utils";
import {
  ALL_SITUATIONS_COMPROVANTES_SINDICATO,
  type SituacaoComprovanteSindicato,
} from "./comprovantesSindicatoTypes";

export const onlyDigits = (v: string | null | undefined) =>
  String(v || "").replace(/\D/g, "");

function normKey(s: string): string {
  return normalizeText(s).replace(/\s+/g, " ");
}

/** Canonicaliza nomes de situação vindos da API para os filtros da listagem. */
export function normalizeSituacaoComprovanteSindicato(
  situacao: string | null | undefined,
): SituacaoComprovanteSindicato | string {
  if (situacao == null || String(situacao).trim() === "") return "-";

  const k = normKey(situacao);

  if (k === "comprovantes pendente" || k === "comprovantes pendentes") {
    return "Comprovantes pendentes";
  }
  if (k === "comprovantes enviado" || k === "comprovantes enviados") {
    return "Comprovantes enviados";
  }
  if (k === "correcao pendente") return "Correção pendente";
  if (k === "assinatura pendente") return "Assinatura pendente";
  if (k === "assinatura expirada") return "Assinatura expirada";
  if (k === "assinatura concluida") return "Assinatura Concluída";
  if (k === "inapto") return "Inapto";

  // Status de conferência (visão analista) → no sindicato aparecem como enviados
  if (k === "conferencia pendente" || k === "conferencia em andamento") {
    return "Comprovantes enviados";
  }

  return String(situacao).trim();
}

export function pickSituacaoComprovanteNome(
  item: Record<string, unknown>,
): string | null {
  const situacao = item?.situacao ?? item?.Situacao;
  if (situacao && typeof situacao === "object") {
    const nome =
      (situacao as { nome?: string; Nome?: string }).nome ??
      (situacao as { Nome?: string }).Nome;
    if (nome) return String(nome);
  }

  const situacaoComprovante =
    item?.situacaoComprovante ??
    item?.SituacaoComprovante ??
    item?.situacaoEnvioComprovante ??
    item?.SituacaoEnvioComprovante ??
    item?.statusComprovante;

  if (situacaoComprovante && typeof situacaoComprovante === "object") {
    const nome =
      (situacaoComprovante as { nome?: string }).nome ??
      (situacaoComprovante as { Nome?: string }).Nome;
    if (nome) return String(nome);
  }

  if (typeof situacaoComprovante === "string" && situacaoComprovante.trim()) {
    return situacaoComprovante;
  }

  if (typeof situacao === "string" && situacao.trim()) {
    return situacao;
  }

  return null;
}

const pickFirstString = (...values: unknown[]): string | null => {
  for (const v of values) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s !== "-") return s;
  }
  return null;
};

const pickBool = (...values: unknown[]): boolean | undefined => {
  for (const v of values) {
    if (typeof v === "boolean") return v;
    if (v === "true" || v === 1 || v === "1") return true;
    if (v === "false" || v === 0 || v === "0") return false;
  }
  return undefined;
};

/**
 * Visão Sindicato: `bloqueado === true` desabilita ação com tooltip de aceite.
 * `bloqueado === false` libera (sindicato inicia o envio).
 */
export function resolveEnvioComprovantesHabilitado(
  item: Record<string, unknown>,
  situacaoNormalizada: string,
): boolean {
  const situacaoKey = normKey(situacaoNormalizada);

  if (situacaoKey === "inapto") return false;

  const bloqueado = pickBool(item.bloqueado, item.Bloqueado);
  if (bloqueado === true) return false;
  if (bloqueado === false) return true;

  // Fallbacks legados (sem flag bloqueado)
  const explicit = pickBool(
    item.podeEnviarComprovantes,
    item.envioComprovantesHabilitado,
    item.aceiteExtrato,
    item.extratoAceito,
  );
  if (explicit === true) return true;
  if (explicit === false) return false;

  return true;
}

/**
 * Visão Analista: com "Comprovantes pendentes" a ação fica desabilitada
 * (aguarda o sindicato iniciar o envio).
 */
export function resolveAcaoHabilitadaAnalista(
  situacaoNormalizada: string,
): boolean {
  const situacaoKey = normKey(situacaoNormalizada);
  if (situacaoKey === "inapto") return false;
  if (situacaoKey.includes("comprovantes pendente")) return false;
  return true;
}

export function pickComprovantesListDates(item: Record<string, unknown>) {
  return {
    envio: pickFirstString(
      item.comprovanteEnviadoEm,
      item.ComprovanteEnviadoEm,
      item.dataEnvioComprovante,
      item.DataEnvioComprovante,
      item.enviadoEm,
      item.EnviadoEm,
    ),
    solicitacaoCorrecao: pickFirstString(
      item.solicitadoCorrecaoEm,
      item.SolicitadoCorrecaoEm,
      item.dataSolicitacaoCorrecaoComprovante,
      item.DataSolicitacaoCorrecaoComprovante,
      item.solicitacaoCorrecaoEm,
      item.SolicitacaoCorrecaoEm,
    ),
    correcao: pickFirstString(
      item.comprovanteCorrigidoEm,
      item.ComprovanteCorrigidoEm,
      item.dataCorrecaoComprovante,
      item.DataCorrecaoComprovante,
      item.correcaoEm,
      item.CorrecaoEm,
    ),
    envioFormulario: pickFirstString(
      item.formularioEnviadoEm,
      item.FormularioEnviadoEm,
      item.dataEnvioFormulario,
      item.DataEnvioFormulario,
      item.envioFormularioEm,
      item.EnvioFormularioEm,
    ),
  };
}

export function rowSituacaoComprovanteMatchesFilters(
  situacaoRow: string | undefined,
  selectedSituation: string[],
  allSituations: readonly string[] = ALL_SITUATIONS_COMPROVANTES_SINDICATO,
): boolean {
  if (selectedSituation.length === 0) return false;
  if (
    selectedSituation.length === allSituations.length &&
    allSituations.every((s) => selectedSituation.includes(s))
  ) {
    return true;
  }

  const rowNorm = normKey(situacaoRow || "");
  return selectedSituation.some((s) => normKey(s) === rowNorm);
}
